import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import {
  Camera,
  Clapperboard,
  PenLine,
  BookOpen,
  Briefcase,
  Search,
} from "lucide-react";
import { getApiBaseUrl } from "../../config/api";
import { resolveAvatarUrl, handleAvatarError } from "../../utils/profilePicUrl";
import { compressVideo, formatBytes } from "../../utils/compressVideo";
import UploadProgressBar from "../shared/UploadProgressBar";
import MentionInput from "../shared/MentionInput";
import { appendMentionedField } from "../../utils/mentions";
import MediaComposePreview, {
  REORDER_DRAG_TYPE,
} from "../shared/MediaComposePreview";
import ArticleForm from "../ArticleForm";
import BlogForm from "../BlogForm";
import HiringForm from "../HiringForm";
import LookingForJobForm from "../LookingForJobForm";
import { isEmployer, canApplyForJobs } from "../../utils/userRole";
import "../PostForm.css";

const API_URL = getApiBaseUrl();
const MEDIA_ACCEPT =
  "image/*,video/*,.jpg,.jpeg,.jpe,.jfif,.pjpeg,.pjp,.png,.gif,.webp,.bmp,.svg,.tif,.tiff,.avif,.heic,.heif,.ico,.mp4,.m4v,.mov,.qt,.webm,.ogg,.ogv,.mpeg,.mpg,.mpe,.avi,.mkv,.3gp,.3g2,.flv,.wmv,.asf,.ts,.m2ts,.mts";
const IMAGE_ACCEPT =
  "image/*,.jpg,.jpeg,.jpe,.jfif,.pjpeg,.pjp,.png,.gif,.webp,.bmp,.svg,.tif,.tiff,.avif,.heic,.heif,.ico";
const VIDEO_ACCEPT =
  "video/*,.mp4,.m4v,.mov,.qt,.webm,.ogg,.ogv,.mpeg,.mpg,.mpe,.avi,.mkv,.3gp,.3g2,.flv,.wmv,.asf,.ts,.m2ts,.mts";

const isImageFile = (file) =>
  file.type.startsWith("image/") ||
  /\.(jpe?g|jpe|jfif|pjpeg|pjp|png|gif|webp|bmp|svg|tiff?|avif|heic|heif|ico)$/i.test(
    file.name
  );

const isVideoFile = (file) =>
  file.type.startsWith("video/") ||
  /\.(mp4|m4v|mov|qt|webm|ogg|ogv|mpe?g|mpg|avi|mkv|3gp|3g2|flv|wmv|asf|ts|m2ts|mts)$/i.test(
    file.name
  );

const isReorderDrag = (e) =>
  Array.from(e.dataTransfer?.types || []).includes(REORDER_DRAG_TYPE);

function GroupPostForm({ user, groupId, groupName, onRefresh }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("General");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [showHiringForm, setShowHiringForm] = useState(false);
  const [showLookingForJobForm, setShowLookingForJobForm] = useState(false);
  const [composerMode, setComposerMode] = useState("text");
  const [isDragging, setIsDragging] = useState(false);
  const previewUrlsRef = useRef(new Map());
  const canPostHiring = isEmployer(user);
  const canPostLookingForJob = canApplyForJobs(user);

  const mediaAccept =
    composerMode === "photo"
      ? IMAGE_ACCEPT
      : composerMode === "video"
        ? VIDEO_ACCEPT
        : MEDIA_ACCEPT;

  const openComposer = (mode = "text") => {
    setComposerMode(mode);
    setShowModal(true);
  };

  const addFiles = (files) =>
    setMediaFiles((prev) => [
      ...prev,
      ...Array.from(files).map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
      })),
    ]);
  const removeFile = (idx) =>
    setMediaFiles((prev) => prev.filter((_, i) => i !== idx));
  const reorderFiles = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setMediaFiles((prev) => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };
  const moveFile = (idx, direction) => reorderFiles(idx, idx + direction);

  const handleFileChange = (e) => addFiles(Array.from(e.target.files));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id || !groupId) return alert("Login and group context required.");

    setLoading(true);
    setUploadProgress(0);
    setSubmitStatus("Preparing post…");
    try {
      const formData = new FormData();
      formData.append("user_id", user.id);
      formData.append("group_id", groupId);
      formData.append("title", title.trim());
      appendMentionedField(formData, "content", content.trim());
      formData.append("category", category);
      formData.append("username", user.username || "");
      formData.append("country", user.country || "");

      const eligibleMedia = mediaFiles.filter((item) => {
        const file = item.file;
        if (composerMode === "photo") return isImageFile(file);
        if (composerMode === "video") return isVideoFile(file);
        return isImageFile(file) || isVideoFile(file);
      });
      const totalMedia = eligibleMedia.length;

      for (let i = 0; i < eligibleMedia.length; i++) {
        const file = eligibleMedia[i].file;
        const prepPct = Math.round(((i + 0.5) / Math.max(totalMedia, 1)) * 40);
        if (isImageFile(file)) {
          try {
            setSubmitStatus(
              totalMedia > 1
                ? `Compressing photo ${i + 1} of ${totalMedia}…`
                : `Compressing photo…`
            );
            setUploadProgress(prepPct);
            const compressed = await imageCompression(file, {
              maxSizeMB: 1.5,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              onProgress: (pct) => {
                setSubmitStatus(
                  totalMedia > 1
                    ? `Compressing photo ${i + 1} of ${totalMedia}… ${pct}%`
                    : `Compressing photo… ${pct}%`
                );
                setUploadProgress(
                  Math.min(
                    39,
                    prepPct + Math.round((pct / 100) * (40 / Math.max(totalMedia, 1)))
                  )
                );
              },
            });
            formData.append("media[]", compressed, compressed.name);
          } catch {
            formData.append("media[]", file);
          }
        } else if (isVideoFile(file)) {
          try {
            const originalSize = formatBytes(file.size);
            setSubmitStatus(
              totalMedia > 1
                ? `Compressing video ${i + 1} of ${totalMedia} (${originalSize})…`
                : `Compressing video (${originalSize})…`
            );
            setUploadProgress(prepPct);
            const result = await compressVideo(file, {
              onProgress: (pct) => {
                setSubmitStatus(
                  totalMedia > 1
                    ? `Compressing video ${i + 1} of ${totalMedia}… ${pct}%`
                    : `Compressing video… ${pct}%`
                );
                setUploadProgress(
                  Math.min(
                    39,
                    prepPct + Math.round((pct / 100) * (40 / Math.max(totalMedia, 1)))
                  )
                );
              },
            });
            const compressedVideo = result.file || result;
            if (result.mode === "server") {
              setSubmitStatus(
                result.message ||
                  `Uploading ${originalSize} — server will convert…`
              );
            } else if (result.mode === "compressed") {
              setSubmitStatus(
                `Compressed ${originalSize} → ${formatBytes(compressedVideo.size)}`
              );
            }
            formData.append("media[]", compressedVideo, compressedVideo.name);
          } catch (err) {
            console.warn("Video prep failed:", err);
            throw new Error(
              err?.message ||
                `Could not prepare "${file.name}" for upload. Use a smaller H.264 MP4 (under 80MB).`
            );
          }
        }
      }

      setSubmitStatus(totalMedia > 0 ? "Uploading media…" : "Posting…");
      setUploadProgress(totalMedia > 0 ? 40 : 10);

      const { data } = await axios.post(
        `${API_URL}/create_group_post.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (event) => {
            const total = event.total || event.loaded;
            if (!total) return;
            const ratio = Math.min(1, event.loaded / total);
            setUploadProgress(Math.min(100, 40 + Math.round(ratio * 60)));
            const pct = Math.round(ratio * 100);
            setSubmitStatus(
              totalMedia > 0
                ? `Uploading media… ${pct}%`
                : `Posting… ${pct}%`
            );
          },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          timeout: 0,
        }
      );

      setUploadProgress(100);
      setSubmitStatus("Finishing…");

      if (data.success) {
        setTitle("");
        setContent("");
        setCategory("General");
        setMediaFiles([]);
        setComposerMode("text");
        setShowModal(false);
        await Promise.resolve(onRefresh?.());
      } else {
        alert(data.message || "Post failed");
      }
    } catch (err) {
      console.error(err);
      alert("Upload error");
    } finally {
      setLoading(false);
      setSubmitStatus("");
      setUploadProgress(null);
    }
  };

  useEffect(() => {
    const urls = previewUrlsRef.current;
    const activeIds = new Set(mediaFiles.map((item) => item.id));

    mediaFiles.forEach((item) => {
      if (!urls.has(item.id)) {
        urls.set(item.id, URL.createObjectURL(item.file));
      }
    });

    for (const [id, url] of urls.entries()) {
      if (!activeIds.has(id)) {
        URL.revokeObjectURL(url);
        urls.delete(id);
      }
    }
  }, [mediaFiles]);

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    },
    []
  );

  const Preview = () => {
    if (mediaFiles.length === 0) return null;
    const items = mediaFiles.map((item) => ({
      id: item.id,
      url: previewUrlsRef.current.get(item.id),
      isVideo: isVideoFile(item.file),
    }));
    return (
      <MediaComposePreview
        items={items}
        onRemove={removeFile}
        onReorder={reorderFiles}
        onMove={moveFile}
      />
    );
  };


  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        margin: 0,
        padding: 0,
        boxSizing: "border-box",
      }}
    >
      <div className="pf-compose">
        <div
          className="pf-compose-top"
          onClick={() => openComposer("text")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") openComposer("text");
          }}
        >
          <img
            src={resolveAvatarUrl(user, API_URL)}
            alt=""
            className="pf-compose-avatar"
            onError={handleAvatarError}
          />
          <div className="pf-compose-prompt">
            What&apos;s on your mind in this group
            {groupName ? `, ${groupName}` : ""}?
          </div>
        </div>

        <div className="pf-compose-actions">
          {[
            {
              key: "photo",
              label: "Photo",
              Icon: Camera,
              onClick: () => {
                setComposerMode("photo");
                const input = document.getElementById("groupMediaInputTriggerPhoto");
                if (input) {
                  input.value = "";
                  input.click();
                }
              },
            },
            {
              key: "video",
              label: "Video",
              Icon: Clapperboard,
              onClick: () => {
                setComposerMode("video");
                const input = document.getElementById("groupMediaInputTriggerVideo");
                if (input) {
                  input.value = "";
                  input.click();
                }
              },
            },
            {
              key: "article",
              label: "Article",
              Icon: PenLine,
              onClick: () => setShowArticleForm(true),
            },
            {
              key: "blog",
              label: "Blog",
              Icon: BookOpen,
              onClick: () => setShowBlogForm(true),
            },
            ...(canPostHiring
              ? [
                  {
                    key: "hiring",
                    label: "Job",
                    Icon: Briefcase,
                    onClick: () => setShowHiringForm(true),
                  },
                ]
              : []),
            ...(canPostLookingForJob
              ? [
                  {
                    key: "looking_for_job",
                    label: "Open to work",
                    Icon: Search,
                    onClick: () => setShowLookingForJobForm(true),
                  },
                ]
              : []),
          ].map((action) => {
            const ActionIcon = action.Icon;
            return (
              <div
                key={action.key}
                className="pf-action"
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    action.onClick();
                  }
                }}
              >
                <span className={`pf-action-icon ${action.key}`}>
                  <ActionIcon size={15} strokeWidth={2.25} />
                </span>
                <span className="pf-action-label">{action.label}</span>
              </div>
            );
          })}
        </div>

        <input
          type="file"
          id="groupMediaInputTriggerPhoto"
          accept={IMAGE_ACCEPT}
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files.length > 0) {
              setComposerMode("photo");
              handleFileChange(e);
              setShowModal(true);
            }
          }}
        />
        <input
          type="file"
          id="groupMediaInputTriggerVideo"
          accept={VIDEO_ACCEPT}
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files.length > 0) {
              setComposerMode("video");
              handleFileChange(e);
              setShowModal(true);
            }
          }}
        />
      </div>

      {showArticleForm && (
        <ArticleForm
          user={user}
          groupId={groupId}
          onClose={() => setShowArticleForm(false)}
          onRefresh={onRefresh}
        />
      )}

      {showBlogForm && (
        <BlogForm
          user={user}
          groupId={groupId}
          onClose={() => setShowBlogForm(false)}
          onRefresh={onRefresh}
        />
      )}

      {showHiringForm && (
        <HiringForm
          user={user}
          groupId={groupId}
          onClose={() => setShowHiringForm(false)}
          onRefresh={onRefresh}
        />
      )}

      {showLookingForJobForm && (
        <LookingForJobForm
          user={user}
          groupId={groupId}
          onClose={() => setShowLookingForJobForm(false)}
          onRefresh={onRefresh}
        />
      )}

      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.55)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            style={{
              background:
                "radial-gradient(circle at top, #f9fafb 0, #e5e7eb 40%, #e2e8f0 100%)",
              borderRadius: 20,
              width: "100%",
              maxWidth: 640,
              padding: 22,
              position: "relative",
              boxShadow:
                "0 24px 60px rgba(15, 23, 42, 0.45), 0 0 0 1px rgba(148, 163, 184, 0.35)",
            }}
          >
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                if (loading) return;
                setShowModal(false);
                setComposerMode("text");
              }}
              style={{
                position: "absolute",
                top: 14,
                right: 18,
                border: "none",
                background: "rgba(15,23,42,0.04)",
                width: 32,
                height: 32,
                borderRadius: "999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                cursor: loading ? "not-allowed" : "pointer",
                color: "#475569",
                opacity: loading ? 0.5 : 1,
                boxShadow: "0 1px 3px rgba(15,23,42,0.22)",
              }}
            >
              ×
            </button>

            <div
              style={{
                marginBottom: 18,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: 18,
                    color: "#0f172a",
                    letterSpacing: 0.2,
                  }}
                >
                  {composerMode === "photo"
                    ? "Create a photo post"
                    : composerMode === "video"
                      ? "Create a video post"
                      : "Create a group post"}
                </h2>
                <p
                  style={{
                    margin: 0,
                    marginTop: 2,
                    fontSize: 12,
                    color: "#64748b",
                  }}
                >
                  {composerMode === "photo"
                    ? "Share one or more photos with this group."
                    : composerMode === "video"
                      ? "Share a video with this group."
                      : `Share thoughts, photos, or videos in ${groupName || "this group"}.`}
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your post a title"
                required
                style={inputStyle}
                disabled={loading}
              />
              <MentionInput
                userId={user?.id}
                value={content}
                onChange={setContent}
                placeholder="What's on your mind? Type @ to mention a connection"
                required
                rows={6}
                disabled={loading}
                style={{
                  border: "1px solid rgba(148, 163, 184, 0.8)",
                  borderRadius: 12,
                  padding: "12px 14px",
                  fontSize: 15,
                  lineHeight: 1.5,
                  resize: "vertical",
                  outline: "none",
                  backgroundColor: "rgba(248, 250, 252, 0.9)",
                  whiteSpace: "pre-wrap",
                  color: "#0f172a",
                  boxShadow:
                    "inset 0 1px 2px rgba(148,163,184,0.35), 0 0 0 1px rgba(255,255,255,0.4)",
                  maxWidth: "100%",
                  opacity: loading ? 0.7 : 1,
                }}
              />

              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={loading}
                style={{
                  ...inputStyle,
                  marginTop: 6,
                  backgroundColor: "#f8fafc",
                }}
              >
                {["General", "Announcement", "Discussion", "Event"].map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>

              <div
                onClick={() => {
                  if (!loading) document.getElementById("groupMediaInput").click();
                }}
                onDragOver={(e) => {
                  if (loading || isReorderDrag(e)) return;
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  if (isReorderDrag(e)) return;
                  e.preventDefault();
                  setIsDragging(false);
                }}
                onDrop={(e) => {
                  if (loading || isReorderDrag(e)) {
                    setIsDragging(false);
                    return;
                  }
                  e.preventDefault();
                  setIsDragging(false);

                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    let files = Array.from(e.dataTransfer.files);
                    if (composerMode === "photo") {
                      files = files.filter(isImageFile);
                    } else if (composerMode === "video") {
                      files = files.filter(isVideoFile);
                    } else {
                      files = files.filter(
                        (f) => isImageFile(f) || isVideoFile(f)
                      );
                    }
                    if (files.length === 0) {
                      alert(
                        composerMode === "photo"
                          ? "Please drop photo files."
                          : composerMode === "video"
                            ? "Please drop video files."
                            : "Please drop photo or video files."
                      );
                      return;
                    }
                    handleFileChange({ target: { files } });
                    e.dataTransfer.clearData();
                  }
                }}
                style={{
                  border: isDragging
                    ? "2px dashed rgba(59,130,246,0.95)"
                    : "2px dashed rgba(148,163,184,0.9)",
                  borderRadius: 14,
                  padding: 18,
                  margin: "8px 0 10px",
                  textAlign: "center",
                  background: isDragging
                    ? "linear-gradient(135deg,#e0f2fe,#ecfeff)"
                    : "rgba(248, 250, 252, 0.95)",
                  cursor: loading ? "not-allowed" : "pointer",
                  color: "#64748b",
                  fontSize: 13,
                  opacity: loading ? 0.6 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                {composerMode === "photo"
                  ? "Drag & drop photos here or "
                  : composerMode === "video"
                    ? "Drag & drop videos here or "
                    : "Drag & drop photos and videos here or "}
                <strong style={{ color: "#0284c7" }}>click to add</strong>
                <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8" }}>
                  {composerMode === "photo"
                    ? "You can add multiple photos and arrange the order below."
                    : composerMode === "video"
                      ? "You can add multiple videos and arrange the order below."
                      : "You can mix photos and videos, then arrange the order below."}
                </div>
              </div>

              <input
                id="groupMediaInput"
                type="file"
                accept={mediaAccept}
                multiple
                style={{ display: "none" }}
                disabled={loading}
                onChange={(e) => {
                  handleFileChange(e);
                  e.target.value = "";
                }}
              />

              <Preview />

              {loading && (
                <UploadProgressBar
                  status={submitStatus}
                  progress={uploadProgress ?? 0}
                />
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 4,
                  background:
                    "linear-gradient(135deg, #0ea5e9, #6366f1, #22c55e)",
                  color: "#f9fafb",
                  border: "none",
                  borderRadius: 999,
                  padding: "11px 20px",
                  width: "100%",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  boxShadow:
                    "0 14px 30px rgba(59,130,246,0.45), 0 0 0 1px rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  fontSize: 13,
                  opacity: loading ? 0.85 : 1,
                }}
              >
                {loading
                  ? uploadProgress !== null
                    ? `Uploading ${uploadProgress}%`
                    : submitStatus || "Posting…"
                  : "Share post"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 14,
  borderRadius: 12,
  border: "1px solid rgba(148, 163, 184, 0.9)",
  marginBottom: 10,
  outline: "none",
  backgroundColor: "rgba(248, 250, 252, 0.95)",
  color: "#0f172a",
  boxShadow:
    "inset 0 1px 2px rgba(148,163,184,0.35), 0 0 0 1px rgba(255,255,255,0.6)",
  boxSizing: "border-box",
  maxWidth: "100%",
};

export default GroupPostForm;
